// The decisions behind the kit's flow pieces: the pager, the fold, the scroll
// panel and the footer (release 3.3, the part of task F2 of the 7 Oct plan
// that Add a vendor and the register need).
//
// # WHY THESE ARE NOT IN THE COMPONENTS
//
// Which page numbers are drawn, what a closed fold says about what is inside
// it, whether the scroll panel shows a shadow at its edge, why a button is
// off: each is a rule a screen relies on, and a rule buried in
// a component can only be checked in a browser. The console's tests run in
// node with no document, so the rules are plain functions here and the
// components draw what they return (the pattern of frame.ts and footers.ts).

import { formatIN } from './motion'

// ----------------------------------------------------------------- pager

// The row sizes a table offers. 25 by default (7 Oct plan, rule 2.1).
export const PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_PAGE_SIZE = 25

// The row size a screen reads from its address (?size=), cleaned: a whole
// number from the sizes offered, else the default. A stale or hand-typed link
// must not reach the range words as "1-0 of 142" or "NaN-NaN of 142", and
// every screen would otherwise write its own parser (review of 12 Oct, S5).
export function cleanSize(raw: unknown, sizes: readonly number[] = PAGE_SIZES): number {
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isInteger(n) && sizes.includes(n) ? n : DEFAULT_PAGE_SIZE
}

// A size the arithmetic can use: a whole number of at least one row.
function usableSize(size: number): number {
  return Number.isInteger(size) && size >= 1 ? size : DEFAULT_PAGE_SIZE
}

export function pageCount(total: number, size: number): number {
  if (!(size > 0)) return 1
  return Math.max(1, Math.ceil(Math.max(0, total) / size))
}

// A page that no longer exists (the list shrank under a filter) becomes the
// last one that does, never an empty page.
export function clampPage(page: number, total: number, size: number): number {
  const last = pageCount(total, size)
  if (!Number.isFinite(page) || page < 1) return 1
  return Math.min(Math.floor(page), last)
}

// "1-25 of 142". With nothing in the list, "0 of 0" would read as broken, so
// it says "None".
export function rangeWords(page: number, size: number, total: number): string {
  if (total <= 0) return 'None'
  const s = usableSize(size)
  const p = clampPage(page, total, s)
  const first = (p - 1) * s + 1
  const last = Math.min(total, p * s)
  return formatIN(first) + '-' + formatIN(last) + ' of ' + formatIN(total)
}

// The page numbers drawn between Previous and Next: the first, the last, the
// current one and `span` either side of it, with a gap where numbers are left
// out. A gap of exactly one number draws the number instead, because "..."
// standing for a single page hides less than it costs.
export function pageWindow(page: number, pages: number, span = 1): (number | 'gap')[] {
  if (pages <= 1) return [1]
  const p = Math.min(Math.max(1, page), pages)
  const keep = new Set<number>([1, pages])
  for (let i = p - span; i <= p + span; i++) if (i >= 1 && i <= pages) keep.add(i)
  const sorted = [...keep].sort((a, b) => a - b)
  const out: (number | 'gap')[] = []
  for (const n of sorted) {
    const prev = out.length ? out[out.length - 1] : undefined
    if (typeof prev === 'number' && n - prev === 2) out.push(prev + 1)
    else if (typeof prev === 'number' && n - prev > 2) out.push('gap')
    out.push(n)
  }
  return out
}

// ------------------------------------------------------------------ fold

// What a closed fold says about what is in it. Hidden settings cause mistakes,
// so a fold whose fields hold values says how many, and names them when there
// are few enough to read in a line: "Country: India, Contract start: 1 Nov
// 2026". Empty values are not counted.
export function foldSummary(values: { label: string; value: string | null | undefined }[], nameUpTo = 2): string {
  const set = values.filter((v) => v.value != null && String(v.value).trim() !== '')
  if (set.length === 0) return 'Nothing set'
  if (set.length <= nameUpTo) return set.map((v) => v.label + ': ' + String(v.value).trim()).join(', ')
  return set.length + ' set'
}

// ---------------------------------------------------------- scroll panel

// Which edges of a scroll panel show a shadow: one where there is more to see
// past that edge. A single pixel of rounding is not "more".
export function scrollEdges(scrollTop: number, scrollHeight: number, clientHeight: number): { top: boolean; bottom: boolean } {
  return {
    top: scrollTop > 1,
    bottom: scrollHeight - clientHeight - scrollTop > 1,
  }
}

// ---------------------------------------------------------------- footer

// One of the footer's actions, in the same shape as the dialog footer's
// (kit/Dialog.tsx on the open dialog branch): a label, what it does, and when
// it is off, why, in words.
export type FlowAction = {
  label: string
  onClick?: () => void
  href?: string
  // The id of a form in the page: the button submits it, so the form's own
  // checks and Enter in a field work.
  form?: string
  off?: string | false | null
  tone?: 'primary' | 'destructive' | 'plain'
  // The persona walk's hooks (data-action, data-action-also).
  actionId?: string
  actionAlso?: string
}

// Whether the action can be pressed, and the words drawn beside it when it
// cannot. A button that is off with no reason given leaves the reader to
// guess, so an off action with no words is given some.
export function actionState(a: FlowAction | undefined, busy = false): { off: boolean; why: string } {
  if (!a) return { off: true, why: '' }
  if (busy) return { off: true, why: '' }
  if (a.off) return { off: true, why: typeof a.off === 'string' && a.off.trim() ? a.off.trim() : 'Not available yet' }
  return { off: false, why: '' }
}
